import { Helmet } from 'react-helmet-async';
import { helmetJsonLdProp } from 'react-schemaorg';
import { Article as SchemaOrgArticle, BlogPosting } from 'schema-dts';

import { useAppContext } from '../AppContext';
import { getAbsoluteUrl, getRouterBasename } from '../util';

export type ArticleSchemaType = 'Article' | 'BlogPosting' | 'NewsArticle';

type ArticleJsonLd = Omit<SchemaOrgArticle | BlogPosting, '@context' | '@type'> & {
  '@type': ArticleSchemaType;
};

export interface ArticleProps {
  authorName?: string;
  dateModified?: string;
  datePublished?: string;
  description?: string;
  headline?: string;
  image?: string;
  schema?: Omit<ArticleJsonLd, '@type'>;
  type?: ArticleSchemaType;
  url?: string;
}

export const Article = ({
  authorName,
  dateModified,
  datePublished,
  description,
  headline,
  image,
  schema,
  type = 'BlogPosting',
  url,
}: ArticleProps) => {
  const { locale, shippingCountry, structuredDataContent } = useAppContext();
  const organization = structuredDataContent?.body.organization;

  const resolvedHeadline = headline?.trim();
  const resolvedDescription = description?.trim();
  const resolvedAuthor = authorName ?? organization?.name;
  const resolvedImage = image ? getAbsoluteUrl(image) : undefined;
  const resolvedUrl = url
    ? getAbsoluteUrl(
        url.startsWith('http')
          ? url
          : `${getRouterBasename(locale, shippingCountry)}${url.startsWith('/') ? url : `/${url}`}`
      )
    : undefined;

  if (!resolvedHeadline) {
    return null;
  }

  return (
    <Helmet
      script={[
        helmetJsonLdProp<ArticleJsonLd>({
          '@context': 'https://schema.org',
          '@type': type,
          author: resolvedAuthor
            ? {
                '@type': 'Organization',
                name: resolvedAuthor,
              }
            : undefined,
          dateModified,
          datePublished,
          description: resolvedDescription,
          headline: resolvedHeadline,
          image: resolvedImage,
          mainEntityOfPage: resolvedUrl,
          publisher: organization?.name
            ? {
                '@type': 'Organization',
                logo: organization.logo?.url
                  ? {
                      '@type': 'ImageObject',
                      url: organization.logo.url,
                    }
                  : undefined,
                name: organization.name,
              }
            : undefined,
          url: resolvedUrl,
          ...schema,
        }),
      ]}
    />
  );
};
